import { AdvancedSelect, SelectionValue } from '../../widgets/select';
import { Dialog, useDialogManager } from '../../widgets/dialog';
import { FormControl } from '../../widgets/formControl';
import { Input } from '../../widgets/input';
import { Link, PrimaryButton } from '../../widgets/buttons';
import { NotificationBanner } from '../../widgets/notificationBanner';
import { removeNotification, showNotification } from '../../widgets/notificationToaster';
import React, { FC, Suspense, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { backofficeUrl, T, toGlobalID } from 'src/shared/helpers';
import styled from 'styled-components';
import config from '../../../shared/config';
import { ApiLabel, ButtonContainer, InstructionItem, InstructionLabel, PointedInstructionsList } from '../../../shared/connectDialogStyle';
import { Loader } from '../../../shared/docsDialog';
import { EcommerceId } from '../../../shared/enums';
import { ValidationMessageStyled } from '../../widgets/validation/validationMessage';
import { Hr } from '../platformSelection/platformConnection';
import { apiIntegrationSalesChannelDialogInsertIntegrationMutation$data } from './__generated__/apiIntegrationSalesChannelDialogInsertIntegrationMutation.graphql';
import { apiIntegrationSalesChannelDialogInsertMutation$data } from './__generated__/apiIntegrationSalesChannelDialogInsertMutation.graphql';
import { blankApiIntegrationSalesChannelDialogCurrenciesQuery } from './__generated__/blankApiIntegrationSalesChannelDialogCurrenciesQuery.graphql';
import { blankApiIntegrationSalesChannelDialogInsertIntegrationMutation } from './__generated__/blankApiIntegrationSalesChannelDialogInsertIntegrationMutation.graphql';
import { blankApiIntegrationSalesChannelDialogInsertMutation } from './__generated__/blankApiIntegrationSalesChannelDialogInsertMutation.graphql';
import { checkUrl } from './helpers';

interface SalesChannelApiProps {
    salesChannelID?: string | undefined;
    hasMasterIntegration?: boolean;
    title?: string;
    zendeskDocId?: string | undefined;
    urlToRedirect?: string | undefined;
    windowDecorator?: FC<any | undefined>;
    onSalesChannelCreated?: () => void;
    onSalesChannelUpdated?: () => void;
}

interface Currency {
    label: string;
    value: string;
}

export const CurrenciesNote = styled.p`
    margin-bottom: 20px;
    margin-top: 5px;
    font-size: 12px;
    color: ${(props) => props.theme.app.primary};
`;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const saveApiSalesChannels = graphql`
    mutation blankApiIntegrationSalesChannelDialogInsertMutation($input: ApiSalesChannelInput!) {
        upsertApiSalesChannel(input: $input) {
            id
            clientID
            secretKey
            salesChannelID
        }
    }
`;
const saveApiIntegration = graphql`
    mutation blankApiIntegrationSalesChannelDialogInsertIntegrationMutation($input: EcommerceSettingsInput!) {
        saveEcommerceSettings(settingsInput: $input) {
            ecommerceID
        }
    }
`;

const BlankApiIntegrationSalesChannelDialog: FC<SalesChannelApiProps> = (salesChannel) => {
    const query = useLazyLoadQuery<blankApiIntegrationSalesChannelDialogCurrenciesQuery>(
        graphql`
            query blankApiIntegrationSalesChannelDialogCurrenciesQuery {
                currencies {
                    id
                    code
                    name
                    symbol
                }
                lcidCurrenciesList {
                    languageCulture
                }
            }
        `,
        {}
    );
    const currenciesList: Currency[] =
        query?.currencies?.map((currency) => {
            return { label: `${currency!.name!} (${currency!.code} ${currency!.symbol})`, value: currency!.id! };
        }) ?? [];

    const defaultCurrency = query?.currencies?.find((currency) => currency?.code === 'USD');

    const [selectedCurrency, setSelectedCurrency] = React.useState<string>(defaultCurrency?.id || '');

    const [storeUrl, setStoreUrl] = React.useState<string>('');
    const [productApiUrl, setProductApiUrl] = React.useState<string>('');
    const [recaptchaToken, setRecaptchaToken] = useState('');

    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();

    const [insertMutation, isInserting] = useMutation<blankApiIntegrationSalesChannelDialogInsertMutation>(saveApiSalesChannels);
    const [saveIntegration, isSavingIntegration] = useMutation<blankApiIntegrationSalesChannelDialogInsertIntegrationMutation>(saveApiIntegration);

    const handleSaveClick = async () => {
        if (selectedCurrency === '') {
            showNotification({
                type: 'danger',
                message: T._('Currency is required.', 'Admin'),
                dismiss: {
                    duration: 5000
                }
            });
            return;
        }

        if (!checkUrl(storeUrl, true)) {
            const loadingNotification = showNotification({
                type: 'default',
                message: T._(`Saving sales channel... this may take a while don't exit the page!`, 'Admin')
            });

            try {
                if (!salesChannel.hasMasterIntegration) {
                    const returnUrlAfterSavingIntegration = encodeURI(backofficeUrl('/sales-channels?added=true&isMaster=true&ecommerceType=API'));
                    await new Promise<apiIntegrationSalesChannelDialogInsertIntegrationMutation$data>((resolve, reject) => {
                        saveIntegration({
                            updater: (store) => store.invalidateStore(),
                            variables: {
                                input: {
                                    currencyGlobalID: selectedCurrency,
                                    ecommerceGlobalID: toGlobalID(EcommerceId.API, 'ECommerce'),
                                    salesChannelID: salesChannel.salesChannelID,
                                    recaptchaToken: recaptchaToken,
                                    remoteUrl: storeUrl
                                }
                            },
                            onCompleted: (data) => {
                                resolve(data);
                                removeNotification(loadingNotification);
                                closeDialog('api-connection');
                                closeDialog('add-sales-channel');
                                window.location.href = backofficeUrl(`/subscription/checkForExisting?returnUrl=${returnUrlAfterSavingIntegration}`);
                            },
                            onError: reject
                        });
                    });

                    if (salesChannel.onSalesChannelCreated) {
                        salesChannel.onSalesChannelCreated();
                    }
                } else {
                    await new Promise<apiIntegrationSalesChannelDialogInsertMutation$data>((resolve, reject) => {
                        insertMutation({
                            updater: (store) => store.invalidateStore(),
                            onCompleted: (data) => {
                                resolve(data);
                                removeNotification(loadingNotification);
                                closeDialog('api-connection');
                                closeDialog('add-sales-channel');
                                navigate(backofficeUrl('/sales-channels?added=true'));
                            },
                            onError: reject,
                            variables: {
                                input: {
                                    id: salesChannel.salesChannelID || '',
                                    storeUrl: storeUrl,
                                    productApiUrl: productApiUrl,
                                    currencyID: selectedCurrency
                                }
                            }
                        });
                    });

                    if (salesChannel.onSalesChannelUpdated) {
                        salesChannel.onSalesChannelUpdated();
                    }
                }
            } catch {
                showNotification({
                    type: 'danger',
                    message: T._('Error saving sales channel.', 'Admin'),
                    dismiss: {
                        duration: 5000
                    }
                });
            } finally {
                removeNotification(loadingNotification);
            }
        }
    };

    const handleSelectedCurrency = (item: SelectionValue<Currency>) => {
        if (item != null && !Array.isArray(item)) {
            setSelectedCurrency(item.value);
        }
    };

    return (
        <Dialog windowDecorator={salesChannel?.windowDecorator} onClose={() => closeDialog('api-connection')} title={salesChannel?.title ?? ''}>
            <Suspense fallback={Loader}>
                <div>
                    <InstructionLabel dangerouslySetInnerHTML={{ __html: `<strong>USE THIS METHOD IF</strong>` }} />
                    <PointedInstructionsList>
                        <InstructionItem
                            dangerouslySetInnerHTML={{
                                __html: `You want to use an eCommerce platform that currently doesn’t integrate with Zakeke (e.g., Vtex, Squarespace, etc.).`
                            }}
                        />
                        <InstructionItem dangerouslySetInnerHTML={{ __html: `You want to connect a custom website to Zakeke.` }} />
                    </PointedInstructionsList>
                    <Hr />
                    <ApiLabel dangerouslySetInnerHTML={{ __html: `<strong><h2>Integration settings</h2></strong>` }} />
                    <ApiLabel
                        dangerouslySetInnerHTML={{
                            __html: `Start by entering the URL of your store and specifying its base currency. For more guidance, refer to the <a href='https://docs.zakeke.com/docs/API/Integration/Integrating-Zakeke' target='_blank' rel='noopener'>integration guide</a>.`
                        }}
                    />
                    <FormControl label={T._('URL of the Store to integrate*', 'Admin')}>
                        <Input
                            required={true}
                            value={storeUrl}
                            placeholder={'Insert your store URL'}
                            onChange={(e) => {
                                setStoreUrl(e.target.value);
                            }}
                        />
                        <ValidationMessageStyled>{checkUrl(storeUrl, true, false)}</ValidationMessageStyled>
                    </FormControl>
                    <FormControl
                        label={T._('Store base currency*', 'Admin')}
                        tooltipLabelHtml={T._(
                            'Enter the currency used in your e-commerce store. Zakeke will use this as the base currency for transaction calculations and all other currency-related operations. Ensure this matches the currency set in your store to avoid discrepancies.',
                            'Admin'
                        )}
                    >
                        <AdvancedSelect
                            placeholder={T._('US Dollar', 'Admin')}
                            options={currenciesList}
                            value={currenciesList.find((option) => option.value == selectedCurrency)}
                            onChange={handleSelectedCurrency}
                            menuPosition={'fixed'}
                        />
                        {/* Uso lo split in quanto le culture potrebbero avere un dialetto particolare(es. "en-US"). In questo modo le lingue con hanno la
						traduzione vengono gestite in modo corretto */}
                        {!query.lcidCurrenciesList?.some((item) => item!.languageCulture?.split('-')[0] === config.culture.split('-')[0]) && (
                            <CurrenciesNote>
                                {T._('Search for a currency by English name or ISO code (e.g., US Dollar or USD, Euro or EUR).', 'Admin')}
                            </CurrenciesNote>
                        )}
                    </FormControl>

                    {!salesChannel.hasMasterIntegration && (
                        <FormControl>
                            <ReCAPTCHA
                                sitekey={config.googleRecaptchaSitekey}
                                theme={'light'}
                                size={'normal'}
                                onChange={(e) => e && setRecaptchaToken(e)}
                            ></ReCAPTCHA>
                        </FormControl>
                    )}

                    <Hr />

                    <NotificationBanner
                        type='INFO'
                        content={
                            <span>
                                <b>{T._('Please note: ', 'Admin')}</b>
                                {T._(
                                    'To complete the integration and allow Zakeke to import your product catalog, load the user interface on your site, and generate production files, you need to integrate our APIs. You can find all the ',
                                    'Admin'
                                )}
                                <Link href='https://docs.zakeke.com/docs/API/Introduction-API' target='_blank'>
                                    {T._('technical documentation here', 'Admin')}
                                </Link>
                                .
                            </span>
                        }
                    ></NotificationBanner>

                    <ButtonContainer>
                        <PrimaryButton onClick={handleSaveClick} disabled={isInserting || isSavingIntegration}>
                            {isInserting || isSavingIntegration ? T._('Saving...', 'Admin') : T._('Save', 'Admin')}
                        </PrimaryButton>
                    </ButtonContainer>
                </div>
            </Suspense>
        </Dialog>
    );
};

export default BlankApiIntegrationSalesChannelDialog;
